import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import workerUrlRaw from '@datadog/browser-worker?url';
import { useHubble } from '@seek/hubble';
import { useRef, useEffect, useMemo } from 'react';

import { useAppConfig } from 'src/App/providers/AppConfigProvider';

import { DatadogRumContext } from './Context';
import type { ContextType } from './types';

interface Props {
  children: React.ReactNode;
}

export const DatadogRumProvider = ({ children }: Props) => {
  const hasInjected = useRef<boolean>(false);
  const { appVersion, environment, userId, userUuid, isAuthenticated } =
    useAppConfig();
  const visitorId = useHubble().visitorId();

  useEffect(() => {
    // If you need to test DD RUM locally, comment out the `|| environment...` bit
    if (hasInjected.current || environment === 'development') {
      return;
    }

    const workerUrl = new URL(workerUrlRaw, window.location.origin).pathname;

    if (!workerUrl) {
      return;
    }

    datadogRum.init({
      applicationId: 'dcfea266-4184-4bb5-a5fe-e460639d8c92',
      clientToken: 'pub08f692ff9f31a8cdcba4e28460fba8c1',
      site: 'datadoghq.com',
      service: 'candidate-basic-search',
      env: environment,
      version: appVersion,
      sessionSampleRate: 10,
      sessionReplaySampleRate: 10,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin() as any],
      compressIntakeRequests: true,
      beforeSend: (event) => {
        if (
          event.type === 'error' &&
          event.error.message.startsWith('csp_violation:')
        ) {
          try {
            datadogRum.addAction('csp_violation', {
              level: 'warning',
              errorMessage: event.error.message,
            });
          } catch {}
          return false;
        }
        return true;
      },
      workerUrl,
    });

    if (userId) {
      datadogRum.setUser({ id: userId });
    }

    datadogRum.setGlobalContext({
      ...datadogRum.getGlobalContext(),
      team: 'talent-search',
      visitorId,
      userUuid,
      isAuthenticated,
      // TODO: isTestUser
    });

    datadogRum.startSessionReplayRecording();

    hasInjected.current = true;
  }, [appVersion, environment, isAuthenticated, userId, userUuid, visitorId]);

  const context = useMemo<ContextType>(
    () => ({
      logError: (error, errorContext) => {
        datadogRum.addError(error, errorContext);
      },
    }),
    [],
  );

  return (
    <DatadogRumContext.Provider value={context}>
      {children}
    </DatadogRumContext.Provider>
  );
};
